import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import Sidebar from "../../component/Sidebar/SideBar";
import Container from "react-bootstrap/esm/Container";
import Row from "react-bootstrap/esm/Row";
import Col from "react-bootstrap/esm/Col";
import DataTables from "../../component/DataTable";
import CouponModal from "../../component/CouponModal";
import ConfirmationModal from "../../component/ConfirmationModal";
import getCoupons from "../../services/coupons/getCoupons";
import createCoupon from "../../services/coupons/createCoupon";
import updateCoupon from "../../services/coupons/updateCoupon";
import deleteCoupon from "../../services/coupons/deleteCoupon";
import { getAllPartner } from "../../services/partner/getAllPartner";
import { toast } from "react-toastify";
import { FaSearch } from "react-icons/fa";
import { FiEdit2, FiTrash2 } from "react-icons/fi";
import { AUTH_TOKEN_KEY } from "../../constants/localstorageKeys";

const initialForm = {
  code: "",
  partnerId: "",
  agencyName: "",
  discountValue: "10",
  status: "active",
  sourceChannel: "whatsapp",
  oneTimeUse: true,
  validFrom: "",
  validTo: "",
};

const buildCouponCodePreview = (agencyName = "", discountValue = "") => {
  const normalizedName = String(agencyName)
    .normalize("NFKD")
    .replace(/[\u0300-\u036f]/g, "")
    .toUpperCase()
    .replace(/[^A-Z0-9]+/g, " ")
    .trim();
  const nameParts = normalizedName.split(/\s+/).filter(Boolean);
  const namePrefix = nameParts.length > 1
    ? nameParts.map((part) => part[0]).join("")
    : nameParts[0] || "";
  const prefix = namePrefix.slice(0, 12);

  if (!prefix) {
    return "";
  }

  const numericDiscount = Number(discountValue);
  const discountSuffix = Number.isFinite(numericDiscount)
    ? String(Math.round(numericDiscount))
    : "";

  return `${prefix}${discountSuffix}`.slice(0, 36);
};

const getCouponErrorMessage = (error, fallbackMessage) => {
  const status = error?.response?.status || error?.statusCode;
  const rawMessage = error?.response?.data?.message || error?.message || "";

  if (status === 401) {
    localStorage.removeItem(AUTH_TOKEN_KEY);
    return rawMessage || "Please login again to manage coupons.";
  }

  if (/redeem_by/i.test(rawMessage) || /timestamp.*past/i.test(rawMessage)) {
    return "Coupon expiry date must be today or a future date. Please choose a valid Valid To date.";
  }

  return rawMessage || fallbackMessage;
};

const CouponManagement = () => {
  const [coupons, setCoupons] = useState([]);
  const [partners, setPartners] = useState([]);
  const [partnersLoading, setPartnersLoading] = useState(true);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [sortField, setSortField] = useState("createdAt");
  const [sortOrder, setSortOrder] = useState("desc");
  const [formOpen, setFormOpen] = useState(false);
  const [form, setForm] = useState(initialForm);
  const [editingCouponId, setEditingCouponId] = useState("");
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [submitAttempted, setSubmitAttempted] = useState(false);
  const [codeManuallyEdited, setCodeManuallyEdited] = useState(false);
  const [pageSize, setPageSize] = useState(10);
  const [totalRows, setTotalRows] = useState(0);
  const [resetPagination, setResetPagination] = useState(false);
  const searchTimer = useRef(null);
  const partnerSearchTimer = useRef(null);

  const discountError = useMemo(() => {
    if (form.discountValue === "") return "";

    const numericValue = Number(form.discountValue);

    if (!Number.isFinite(numericValue)) {
      return "Discount percentage must be a valid number.";
    }

    if (numericValue < 5 || numericValue > 100) {
      return "Discount percentage must be between 5 and 100.";
    }

    return "";
  }, [form.discountValue]);

  const fieldErrors = useMemo(() => ({
    code: form.code.trim() ? "" : "Coupon code is required.",
    partnerId: form.partnerId ? "" : "Partner is required.",
    discountValue: form.discountValue === "" ? "Discount % is required." : discountError,
    validFrom: form.validFrom ? "" : "Valid From is required.",
    validTo: form.validTo ? "" : "Valid To is required.",
  }), [discountError, form.code, form.discountValue, form.partnerId, form.validFrom, form.validTo]);

  const loadPartners = useCallback((partnerSearch = "") => {
    clearTimeout(partnerSearchTimer.current);
    partnerSearchTimer.current = setTimeout(async () => {
      try {
        setPartnersLoading(true);
        const response = await getAllPartner({ search: partnerSearch, page: 1, limit: 20 });
        setPartners(response?.data || []);
      } catch (error) {
        toast.error(getCouponErrorMessage(error, "Failed to fetch partners"));
      } finally {
        setPartnersLoading(false);
      }
    }, partnerSearch ? 300 : 0);
  }, []);

  useEffect(() => {
    loadPartners();
  }, [loadPartners]);

  const loadCoupons = useCallback(async (nextSearch = search, nextSortField = sortField, nextSortOrder = sortOrder, nextPage = 1, nextLimit = pageSize) => {
    try {
      setLoading(true);
      const response = await getCoupons({
        search: nextSearch,
        sortField: nextSortField,
        sortOrder: nextSortOrder,
        page: nextPage,
        limit: nextLimit,
      });
      setCoupons(response?.data || []);
      setTotalRows(response?.pagination?.totalRows || 0);
    } catch (error) {
      toast.error(getCouponErrorMessage(error, "Failed to fetch coupons"));
    } finally {
      setLoading(false);
    }
  }, [search, sortField, sortOrder, pageSize]);

  useEffect(() => {
    loadCoupons();
  }, []);

  const handleSort = async (column, direction) => {
    const nextSortField = column.key || "createdAt";
    const nextSortOrder = direction === "asc" ? "asc" : "desc";
    setSortField(nextSortField);
    setSortOrder(nextSortOrder);
    setResetPagination((value) => !value);
    await loadCoupons(search, nextSortField, nextSortOrder, 1);
  };

  const handleSearchChange = (event) => {
    const value = event.target.value;
    setSearch(value);
    clearTimeout(searchTimer.current);
    searchTimer.current = setTimeout(() => {
      setResetPagination((current) => !current);
      loadCoupons(value, sortField, sortOrder, 1);
    }, 300);
  };

  const resetForm = () => {
    setForm(initialForm);
    setEditingCouponId("");
    setCodeManuallyEdited(false);
    setSubmitAttempted(false);
    setFormOpen(false);
  };

  const updateFormField = (field, value) => {
    if (field === "code") {
      setCodeManuallyEdited(Boolean(value.trim()));
      setForm((prev) => ({ ...prev, code: value.toUpperCase() }));
      return;
    }

    if (field === "partnerId") {
      const agencyName = partners.find((partner) => partner._id === value)?.companyName || "";
      setForm((prev) => {
        const nextForm = {
          ...prev,
          partnerId: value,
          agencyName,
        };

        if (!editingCouponId && !codeManuallyEdited) {
          nextForm.code = buildCouponCodePreview(
            nextForm.agencyName,
            nextForm.discountValue
          );
        }

        return nextForm;
      });
      return;
    }

    setForm((prev) => {
      const nextForm = { ...prev, [field]: value };

      if (
        !editingCouponId &&
        !codeManuallyEdited &&
        field === "discountValue"
      ) {
        nextForm.code = buildCouponCodePreview(
          nextForm.agencyName,
          nextForm.discountValue
        );
      }

      return nextForm;
    });
  };

  const handleSubmit = async () => {
    try {
      setSubmitAttempted(true);
      setSaving(true);

      if (Object.values(fieldErrors).some(Boolean)) {
        return;
      }

      const payload = {
        ...form,
        code: form.code.trim().toUpperCase(),
      };

      const response = editingCouponId
        ? await updateCoupon(editingCouponId, payload)
        : await createCoupon(payload);

      toast.success(response?.message || "Coupon saved successfully");
      resetForm();
      await loadCoupons();
    } catch (error) {
      toast.error(getCouponErrorMessage(error, "Failed to save coupon"));
    } finally {
      setSaving(false);
    }
  };

  const startCreate = () => {
    setEditingCouponId("");
    setForm(initialForm);
    setCodeManuallyEdited(false);
    setSubmitAttempted(false);
    setFormOpen(true);
  };

  const startEdit = (coupon) => {
    if (coupon.partnerId && !partners.some((partner) => partner._id === coupon.partnerId)) {
      setPartners((current) => [
        ...current,
        { _id: coupon.partnerId, companyName: coupon.agencyName || "Selected partner" },
      ]);
    }
    setEditingCouponId(coupon._id);
    setForm({
      code: coupon.code || "",
      partnerId: coupon.partnerId || "",
      agencyName: coupon.agencyName || "",
      discountValue: String(coupon.discountValue || ""),
      status: coupon.status || "active",
      sourceChannel: coupon.sourceChannel || "whatsapp",
      oneTimeUse: Boolean(coupon.oneTimeUse),
      validFrom: coupon.validFrom ? new Date(coupon.validFrom).toISOString().split("T")[0] : "",
      validTo: coupon.validTo ? new Date(coupon.validTo).toISOString().split("T")[0] : "",
    });
    setCodeManuallyEdited(true);
    setSubmitAttempted(false);
    setFormOpen(true);
  };

  const handleDeleteCoupon = (coupon) => {
    setDeleteTarget(coupon);
  };

  const closeDeleteModal = () => {
    if (deleting) return;
    setDeleteTarget(null);
  };

  const confirmDeleteCoupon = async () => {
    if (!deleteTarget?._id) return;

    try {
      setDeleting(true);
      const response = await deleteCoupon(deleteTarget._id);
      toast.success(response?.message || "Coupon deleted successfully");
      setDeleteTarget(null);
      await loadCoupons();
    } catch (error) {
      toast.error(getCouponErrorMessage(error, "Failed to delete coupon"));
    } finally {
      setDeleting(false);
    }
  };

  const columns = [
    {
      name: <span className="dataTable-text">Code</span>,
      key: "code",
      cell: (row) => <span className="text-start column-text fw-semibold">{row.code}</span>,
      sortable: true,
      width: "140px",
    },
    {
      name: <span className="dataTable-text">Agency</span>,
      key: "agencyName",
      cell: (row) => (
        <span className="text-start column-text">
          {row.agencyName || "-"}
        </span>
      ),
      width: "210px",
    },
    {
      name: <span className="dataTable-text">Discount</span>,
      key: "discountValue",
      cell: (row) => <span className="text-start column-text">{row.discountValue}%</span>,
      sortable: true,
      width: "120px",
    },
    {
      name: <span className="dataTable-text">Channel</span>,
      key: "sourceChannel",
      cell: (row) => <span className="text-start column-text text-capitalize">{row.sourceChannel}</span>,
      width: "120px",
    },
    {
      name: <span className="dataTable-text">Status</span>,
      key: "status",
      cell: (row) => <span className="text-start column-text text-capitalize">{row.status}</span>,
      sortable: true,
      width: "120px",
    },
    {
      name: <span className="dataTable-text">Per Customer</span>,
      key: "oneTimeUse",
      cell: (row) => <span className="text-start column-text">{row.oneTimeUse ? "Yes" : "No"}</span>,
      width: "130px",
    },
    {
      name: <span className="dataTable-text">Usage</span>,
      key: "usedCount",
      cell: (row) => (
        <span className="text-start column-text">
          {row.usedCount}{row.usageLimit ? ` / ${row.usageLimit}` : ""}
        </span>
      ),
      sortable: true,
      width: "90px",
    },
    {
      name: <span className="dataTable-text">Stripe Sync</span>,
      key: "stripeCouponId",
      cell: (row) => (
        <span className="text-start column-text">
          {row.stripeCouponId && row.stripePromotionCodeId ? "Synced" : "Pending"}
        </span>
      ),
      width: "120px",
    },
    {
      name: <span className="dataTable-text">Action</span>,
      key: "action",
      cell: (row) => (
        <div className="d-flex align-items-center gap-2">
          <button
            type="button"
            className="btn btn-sm btn-light border d-flex align-items-center justify-content-center"
            onClick={() => startEdit(row)}
            title="Edit coupon"
            aria-label="Edit coupon"
          >
            <FiEdit2 size={16} />
          </button>
          <button
            type="button"
            className="btn btn-sm btn-light border text-danger d-flex align-items-center justify-content-center"
            onClick={() => handleDeleteCoupon(row)}
            title="Delete coupon"
            aria-label="Delete coupon"
          >
            <FiTrash2 size={16} />
          </button>
        </div>
      ),
      width: "120px",
    },
  ];

  return (
    <Sidebar>
      <Container>
        <Row>
          <Col md={12} className="text-start p-0">
            <h1 className="package-h2">Coupon Management</h1>
          </Col>
          <Col md={12} className="text-start p-0 mt-2">
            <p className="mb-0 text-muted">
              Create and manage agency or partner promo codes for WhatsApp guest checkout.
            </p>
          </Col>
        </Row>

        <Row className="mt-4">
          <Col md={12} className="script-box text-start p-4">
            <div className="d-flex justify-content-between align-items-center gap-3 flex-wrap mb-3">
              <div className="search-input d-flex align-items-center" style={{ maxWidth: 340 }}>
                <input
                  type="text"
                  placeholder="Search coupon code"
                  className="offdK-search ps-3"
                  value={search}
                  onChange={handleSearchChange}
                />
                <FaSearch size={18} color="#215B5B" />
              </div>
              <button className="btn-add-new btn-add-new-font" onClick={startCreate}>
                + Add Coupon
              </button>
            </div>

            <DataTables
              columns={columns}
              data={coupons}
              loading={loading}
              handleSort={handleSort}
              paginationServer
              paginationTotalRows={totalRows}
              paginationPerPage={pageSize}
              paginationResetDefaultPage={resetPagination}
              onChangePage={(nextPage) => loadCoupons(search, sortField, sortOrder, nextPage)}
              onChangeRowsPerPage={(nextLimit) => {
                setPageSize(nextLimit);
                setResetPagination((value) => !value);
                loadCoupons(search, sortField, sortOrder, 1, nextLimit);
              }}
            />
          </Col>
        </Row>

        <CouponModal
          formOpen={formOpen}
          resetForm={resetForm}
          editingCouponId={editingCouponId}
          form={form}
          setForm={setForm}
          updateFormField={updateFormField}
          fieldErrors={fieldErrors}
          submitAttempted={submitAttempted}
          discountError={discountError}
          handleSubmit={handleSubmit}
          saving={saving}
          partners={partners}
          partnersLoading={partnersLoading}
          onPartnerSearch={loadPartners}
        />

        <ConfirmationModal
          modalShow={Boolean(deleteTarget)}
          deleteResource={confirmDeleteCoupon}
          confirmationModalClose={closeDeleteModal}
          loading={deleting}
          title={`Delete coupon ${deleteTarget?.code || ""}?`}
          confirmText="Delete"
        />
      </Container>
    </Sidebar>
  );
};

export default CouponManagement;
